import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import { getTrackerProducts } from "../../services/trackerProductService";
import "./TrackerProductsModal.css";

const PAGE_SIZE = 20;

function formatPrice(value) {
    const numericValue = Number(value ?? 0);

    return new Intl.NumberFormat("es-PE", {
        style: "currency",
        currency: "PEN",
        minimumFractionDigits: 2,
    }).format(
        Number.isFinite(numericValue)
            ? numericValue
            : 0
    );
}

function hasDiscount(value) {
    return (
        value === true ||
        value === 1 ||
        value === "1" ||
        value === "true"
    );
}

export default function TrackerProductsModal({
    open,
    token,
    userName,
    onClose,
}) {
    const [products, setProducts] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const scrollContainerRef = useRef(null);
    const observerRef = useRef(null);
    const loadingRef = useRef(false);
    const pageRef = useRef(1);
    const hasMoreRef = useRef(true);
    const abortControllerRef = useRef(null);

    const loadProducts = useCallback(
        async (pageToLoad, replace = false) => {
            if (
                !token ||
                loadingRef.current ||
                (!replace && !hasMoreRef.current)
            ) {
                return;
            }

            loadingRef.current = true;
            setLoading(true);
            setError("");

            const controller = new AbortController();
            abortControllerRef.current = controller;

            try {
                const response = await getTrackerProducts({
                    token,
                    page: pageToLoad,
                    limit: PAGE_SIZE,
                    signal: controller.signal,
                });

                setProducts((currentProducts) => {
                    if (replace) {
                        return response.data;
                    }

                    const existingIds = new Set(
                        currentProducts.map((product) =>
                            String(product.id)
                        )
                    );

                    const newProducts = response.data.filter(
                        (product) =>
                            !existingIds.has(String(product.id))
                    );

                    return [
                        ...currentProducts,
                        ...newProducts,
                    ];
                });

                pageRef.current = pageToLoad;
                hasMoreRef.current =
                    response.pagination.has_more;

                setPage(pageToLoad);
                setHasMore(response.pagination.has_more);
            } catch (requestError) {
                if (requestError.name !== "AbortError") {
                    setError(
                        requestError.message ??
                            "No se pudieron cargar los productos"
                    );
                }
            } finally {
                if (!controller.signal.aborted) {
                    loadingRef.current = false;
                    setLoading(false);
                }
            }
        },
        [token]
    );

    useEffect(() => {
        if (!open || !token) {
            return undefined;
        }

        setProducts([]);
        setPage(1);
        setHasMore(true);
        setError("");

        pageRef.current = 1;
        hasMoreRef.current = true;
        loadingRef.current = false;

        loadProducts(1, true);

        return () => {
            abortControllerRef.current?.abort();
            observerRef.current?.disconnect();
        };
    }, [open, token, loadProducts]);

    const lastProductRef = useCallback(
        (node) => {
            observerRef.current?.disconnect();

            if (
                !node ||
                loadingRef.current ||
                !hasMoreRef.current ||
                !scrollContainerRef.current
            ) {
                return;
            }

            observerRef.current = new IntersectionObserver(
                (entries) => {
                    const entry = entries[0];

                    if (
                        entry.isIntersecting &&
                        !loadingRef.current &&
                        hasMoreRef.current
                    ) {
                        loadProducts(pageRef.current + 1);
                    }
                },
                {
                    root: scrollContainerRef.current,
                    rootMargin: "200px",
                    threshold: 0.1,
                }
            );

            observerRef.current.observe(node);
        },
        [loadProducts]
    );

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "hidden";

        return () => {
            document.removeEventListener(
                "keydown",
                handleKeyDown
            );

            document.body.style.overflow = "";
        };
    }, [open, onClose]);

    if (!open) {
        return null;
    }

    const handleOverlayClick = (event) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    };

    return (
        <div
            className="tracker-products-overlay"
            onMouseDown={handleOverlayClick}
            role="presentation"
        >
            <section
                className="tracker-products-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="tracker-products-title"
            >
                <header className="tracker-products-header">
                    <div>
                        <h2 id="tracker-products-title">
                            Productos visualizados
                        </h2>

                        <p>
                            {userName || "Usuario"}
                        </p>
                    </div>

                    <button
                        type="button"
                        className="tracker-products-close"
                        onClick={onClose}
                        aria-label="Cerrar modal"
                    >
                        ×
                    </button>
                </header>

                <div className="tracker-products-token">
                    <span>Token:</span>
                    <strong>{token}</strong>
                </div>

                <div
                    ref={scrollContainerRef}
                    className="tracker-products-content"
                >
                    <div className="tracker-products-grid">
                        {products.map((product, index) => {
                            const isLastProduct =
                                index === products.length - 1;

                            const discounted = hasDiscount(
                                false
                            );

                            const originalPrice = Number(
                                product.price ?? 0
                            );

                            const discountPrice = Number(
                                product.precioDescuento ?? 0
                            );

                            const finalPrice =
                                discounted &&
                                discountPrice > 0
                                    ? discountPrice
                                    : originalPrice;

                            return (
                                <article
                                    ref={
                                        isLastProduct
                                            ? lastProductRef
                                            : null
                                    }
                                    key={
                                        product.id ??
                                        `${product.nombre}-${index}`
                                    }
                                    className="tracker-product-card"
                                >
                                    <div className="tracker-product-image-container">
                                        {product.image ? (
                                            <img
                                                src={product.image}
                                                alt={
                                                    product.nombre ??
                                                    "Producto"
                                                }
                                                className="tracker-product-image"
                                                loading="lazy"
                                                onError={(event) => {
                                                    event.currentTarget.style.display =
                                                        "none";

                                                    event.currentTarget
                                                        .nextElementSibling
                                                        ?.classList.remove(
                                                            "hidden"
                                                        );
                                                }}
                                            />
                                        ) : null}

                                        <div
                                            className={`tracker-product-image-empty ${
                                                product.image
                                                    ? "hidden"
                                                    : ""
                                            }`}
                                        >
                                            Sin imagen
                                        </div>

                                        {discounted && (
                                            <span className="tracker-product-discount-badge">
                                                Oferta
                                            </span>
                                        )}
                                    </div>

                                    <div className="tracker-product-information">
                                        <h3 title={product.nombre}>
                                            {product.name ||
                                                "Producto sin nombre"}
                                        </h3>

                                        <div className="tracker-product-prices">
                                            {discounted &&
                                                discountPrice > 0 && (
                                                    <span className="tracker-product-original-price">
                                                        {formatPrice(
                                                            originalPrice
                                                        )}
                                                    </span>
                                                )}

                                            <strong className="tracker-product-final-price">
                                                {formatPrice(
                                                    finalPrice
                                                )}
                                            </strong>
                                        </div>

                                        <div className="tracker-product-details">
                                            <div>
                                                <span>
                                                    Cantidad
                                                </span>

                                                <strong>
                                                    {Number(
                                                        product.cantidad ??
                                                            0
                                                    )}
                                                </strong>
                                            </div>

                                            <div>
                                                <span>
                                                    Descuento
                                                </span>

                                                <strong
                                                    className={
                                                        discounted
                                                            ? "discount-active"
                                                            : "discount-inactive"
                                                    }
                                                >
                                                    {discounted
                                                        ? "Sí"
                                                        : "No"}
                                                </strong>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>

                    {!loading &&
                        products.length === 0 &&
                        !error && (
                            <div className="tracker-products-empty">
                                No existen productos registrados para
                                esta sesión.
                            </div>
                        )}

                    {error && (
                        <div className="tracker-products-error">
                            <p>{error}</p>

                            <button
                                type="button"
                                onClick={() =>
                                    loadProducts(
                                        products.length === 0
                                            ? 1
                                            : page + 1,
                                        products.length === 0
                                    )
                                }
                            >
                                Reintentar
                            </button>
                        </div>
                    )}

                    {loading && (
                        <div className="tracker-products-loading">
                            <span className="tracker-products-spinner" />
                            Cargando productos...
                        </div>
                    )}

                    {!loading &&
                        !hasMore &&
                        products.length > 0 && (
                            <div className="tracker-products-end">
                                No hay más productos.
                            </div>
                        )}
                </div>
            </section>
        </div>
    );
}